import { useCallback, useEffect, useState } from "react";
import { Plus, ArrowUp, ArrowDown, Eye, EyeOff, Trash2, Save, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { errMsg, mediaUrl, uid } from "@/lib/api";
import { useApi, PageHead, UploadButton } from "@/admin/AdminApp";
import { Confirm } from "@/admin/Families";
import { SectionView } from "@/components/SectionView";

const TYPES = {
  text: ["Text", [["heading", "Heading"], ["body", "Body", "area"]]],
  image: ["Image", [["caption", "Caption"]]],
  gallery: ["Photo gallery", []],
  swatches: ["Colour swatches", [["title", "Title"]]],
  button: ["Button", [["label", "Label"], ["url", "Link (URL or #section)"]]],
  file: ["File download", [["label", "Label"]]],
  day: ["Itinerary day", [["date_label", "Date (e.g. 28 Jan)"], ["weekday", "Weekday"], ["title", "Title"]]],
  card: ["Highlight card", [["eyebrow", "Eyebrow"], ["title", "Title"], ["subtitle", "Subtitle"], ["body", "Body", "area"], ["link", "Link (#section)"]]],
  contact: ["Contact", [["name", "Name"], ["role", "Role"], ["phone", "Phone"]]],
  anchor: ["Anchor (invisible link target)", [["name", "Anchor name — link to it with #name"]]],
};

const Inp = ({ label, value, onChange, area, testId }) => (
  <label className="block"><span className="label-lux">{label}</span>
    {area ? <textarea className="input-lux min-h-[90px]" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
      : <input className="input-lux" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />}
  </label>
);

const ListEditor = ({ items = [], onChange, blank, render, addLabel, testId }) => (
  <div className="space-y-2">
    {items.map((it, i) => (
      <div key={it._k || `${i}-${items.length}`} className="flex items-start gap-2 rounded-xl border border-[var(--line)] p-3">
        <div className="flex-1">{render(it, (patch) => onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x))))}</div>
        <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="p-1 text-slate-400 hover:text-red-700" data-testid={`${testId}-remove-${i}`}><X size={16} /></button>
      </div>
    ))}
    <button type="button" className="btn-ghost !py-1.5 text-sm" onClick={() => onChange([...items, blank()])} data-testid={`${testId}-add`}><Plus size={14} /> {addLabel}</button>
  </div>
);

const SectionEditor = ({ s, onChange, tid, targets = [] }) => {
  const d = s.data || {};
  const set = (patch) => onChange({ ...s, data: { ...d, ...patch } });
  return (
    <div className="space-y-3">
      {TYPES[s.type][1].map(([k, l, a]) => <Inp key={k} label={l} area={a} value={d[k]} onChange={(v) => set({ [k]: v })} testId={`${tid}-${k}`} />)}
      {s.type === "card" && (
        <label className="block"><span className="label-lux">Colour</span>
          <select className="input-lux" value={d.tone || "ivory"} onChange={(e) => set({ tone: e.target.value })} data-testid={`${tid}-tone`}>
            <option value="wine">Wine</option><option value="olive">Olive</option><option value="ivory">Ivory</option>
          </select></label>
      )}
      {(s.type === "image" || s.type === "file") && (
        <div className="flex flex-wrap items-center gap-3">
          {d.url && s.type === "image" && <img src={mediaUrl(d.url)} alt="" className="h-20 w-20 rounded-lg object-cover" />}
          {d.url && s.type === "file" && <span className="text-sm text-slate-600">{d.name}</span>}
          <UploadButton accept={s.type === "image" ? "image/*" : "*"} label={d.url ? "Replace" : "Upload"} testId={`${tid}-upload`}
            onUploaded={(r) => set({ url: r.url, name: r.name, label: d.label || r.name })} />
        </div>
      )}
      {s.type === "gallery" && (
        <div>
          <div className="mb-3 flex flex-wrap gap-2">
            {(d.images || []).map((u, i) => (
              <div key={`${u}-${i}`} className="relative">
                <img src={mediaUrl(u)} alt="" className="h-20 w-16 rounded-lg object-cover" />
                <button type="button" onClick={() => set({ images: d.images.filter((_, j) => j !== i) })} className="absolute -right-2 -top-2 rounded-full bg-white p-0.5 shadow" data-testid={`${tid}-img-remove-${i}`}><X size={12} /></button>
              </div>
            ))}
          </div>
          <UploadButton multiple label="Add photos" testId={`${tid}-upload`}
            onUploaded={(r) => onChange((cur) => ({ ...cur, data: { ...cur.data, images: [...(cur.data?.images || []), r.url] } }))} />
        </div>
      )}
      {s.type === "swatches" && (
        <ListEditor items={d.colors} onChange={(colors) => set({ colors })} blank={() => ({ _k: uid(), hex: "#C5A059", name: "" })} addLabel="Colour" testId={`${tid}-colors`}
          render={(c, up) => (
            <div className="flex items-center gap-2">
              <input type="color" value={c.hex} onChange={(e) => up({ hex: e.target.value })} className="h-10 w-12 rounded" />
              <input className="input-lux" placeholder="Name" value={c.name} onChange={(e) => up({ name: e.target.value })} />
            </div>
          )} />
      )}
      {s.type === "day" && (
        <ListEditor items={d.events} onChange={(events) => set({ events })} blank={() => ({ _k: uid(), time: "", title: "", place: "", note: "" })} addLabel="Event" testId={`${tid}-events`}
          render={(e, up) => (
            <div className="grid gap-2 sm:grid-cols-2">
              <input className="input-lux" placeholder="Time" value={e.time} onChange={(x) => up({ time: x.target.value })} />
              <input className="input-lux" placeholder="Title" value={e.title} onChange={(x) => up({ title: x.target.value })} />
              <input className="input-lux" placeholder="Place (opens Google Maps)" value={e.place} onChange={(x) => up({ place: x.target.value })} />
              <input className="input-lux" placeholder="Note" value={e.note} onChange={(x) => up({ note: x.target.value })} />
              <select className="input-lux sm:col-span-2" value={e.link || ""} onChange={(x) => up({ link: x.target.value })} data-testid={`${tid}-event-link-select`}>
                <option value="">Clicking the event opens: nothing</option>
                {targets.map(([v, l]) => <option key={v} value={v}>Opens: {l}</option>)}
              </select>
            </div>
          )} />
      )}
    </div>
  );
};

const PageEditor = ({ page, onSaved, onDelete, targets, parents = [] }) => {
  const api = useApi();
  const [p, setP] = useState(page);
  const [add, setAdd] = useState("text");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setP(page); }, [page]);
  const setSec = (i, s) => setP((cur) => ({ ...cur, sections: cur.sections.map((x, j) => (j === i ? (typeof s === "function" ? s(x) : s) : x)) }));
  const move = (i, d) => setP((cur) => { const a = [...cur.sections]; [a[i], a[i + d]] = [a[i + d], a[i]]; return { ...cur, sections: a }; });
  const save = async () => {
    setBusy(true);
    try { const r = await api("put", `/pages/${p.id}`, { title: p.title, menu_label: p.menu_label, eyebrow: p.eyebrow, hidden: p.hidden, slug: p.slug, parent: p.parent || "", sections: p.sections }); onSaved(r); toast.success("Page saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="page-editor">
      <div className="grid gap-3 rounded-2xl border border-[var(--line)] bg-white p-5 sm:grid-cols-4">
        <Inp label="Title" value={p.title} onChange={(v) => setP({ ...p, title: v })} testId="page-title-input" />
        <Inp label="Menu label" value={p.menu_label} onChange={(v) => setP({ ...p, menu_label: v })} testId="page-menu-input" />
        <Inp label="Eyebrow" value={p.eyebrow} onChange={(v) => setP({ ...p, eyebrow: v })} testId="page-eyebrow-input" />
        {p.system ? <div className="text-sm text-slate-500"><span className="label-lux">Anchor</span>#home</div>
          : <Inp label="Anchor (link with #anchor)" value={p.slug} onChange={(v) => setP({ ...p, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} testId="page-slug-input" />}
        {!p.system && (
          <label className="block sm:col-span-2"><span className="label-lux">Show in guest menu</span>
            <select className="input-lux" value={p.parent || ""} onChange={(e) => setP({ ...p, parent: e.target.value })} data-testid="page-parent-select">
              <option value="">As its own menu item</option>
              {parents.map((x) => <option key={x.id} value={x.slug}>Under “{x.menu_label || x.title}”</option>)}
            </select></label>
        )}
      </div>
      {p.sections.map((s, i) => (
        <div key={s.id} className={`rounded-2xl border border-[var(--line)] bg-white p-5 ${s.hidden ? "opacity-60" : ""}`} data-testid={`section-editor-${i}`}>
          <div className="mb-4 flex items-center justify-between">
            <span className="eyebrow">{TYPES[s.type]?.[0]}</span>
            <div className="flex gap-1">
              <button disabled={i === 0} onClick={() => move(i, -1)} className="rounded-full p-1.5 text-slate-500 disabled:opacity-30" data-testid={`section-up-${i}`}><ArrowUp size={15} /></button>
              <button disabled={i === p.sections.length - 1} onClick={() => move(i, 1)} className="rounded-full p-1.5 text-slate-500 disabled:opacity-30" data-testid={`section-down-${i}`}><ArrowDown size={15} /></button>
              <button onClick={() => setSec(i, { ...s, hidden: !s.hidden })} className="rounded-full p-1.5 text-slate-500" data-testid={`section-hide-${i}`}>{s.hidden ? <EyeOff size={15} /> : <Eye size={15} />}</button>
              <button onClick={() => setP({ ...p, sections: p.sections.filter((_, j) => j !== i) })} className="rounded-full p-1.5 text-red-700" data-testid={`section-delete-${i}`}><Trash2 size={15} /></button>
            </div>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <SectionEditor s={s} onChange={(ns) => setSec(i, ns)} tid={`section-${i}`} targets={targets} />
            <div className="hidden rounded-xl bg-[var(--ivory)] p-4 lg:block"><div className="mb-2 text-[10px] uppercase tracking-[.2em] text-slate-400">Preview</div><SectionView section={s} /></div>
          </div>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-dashed border-[var(--gold)] p-4">
        <select className="input-lux !w-auto" value={add} onChange={(e) => setAdd(e.target.value)} data-testid="add-section-type-select">
          {Object.entries(TYPES).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button className="btn-ghost !py-2 text-sm" onClick={() => setP({ ...p, sections: [...p.sections, { id: uid(), type: add, hidden: false, data: {} }] })} data-testid="add-section-btn"><Plus size={14} /> Add section</button>
      </div>
      <div className="sticky bottom-4 flex justify-between gap-2">
        {!p.system ? <button className="btn-ghost text-red-700" onClick={onDelete} data-testid="delete-page-btn"><Trash2 size={15} /> Delete page</button> : <span />}
        <button className="btn-ink shadow-xl" onClick={save} disabled={busy} data-testid="save-page-btn">{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save page</button>
      </div>
    </div>
  );
};

export default function PagesCms() {
  const api = useApi();
  const [pages, setPages] = useState(null);
  const [cur, setCur] = useState(null);
  const [del, setDel] = useState(false);
  const load = useCallback((selectId) => api("get", "/pages").then((ps) => { setPages(ps); setCur(ps.find((x) => x.id === selectId)?.id || selectId || ps[0]?.id); }), [api]);
  useEffect(() => { load(); }, [load]);
  if (!pages) return <Loader2 className="mx-auto animate-spin text-[var(--gold)]" />;
  const page = pages.find((p) => p.id === cur);
  const label = (p) => p.menu_label || p.title || p.slug || "Untitled page";
  const targets = pages.flatMap((p) => [[`#${p.slug}`, `${label(p)} page`],
    ...(p.sections || []).filter((s) => s.type === "anchor" && s.data?.name).map((s) => {
      const a = s.data.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      return [`#${a}`, `${label(p)} › ${s.data.name}`];
    })]);

  const move = async (i, d) => {
    const ids = pages.map((p) => p.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    try { await api("post", "/pages/reorder", { ids }); load(cur); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (p) => {
    try { await api("put", `/pages/${p.id}`, { ...p, hidden: !p.hidden }); load(cur); } catch (e) { toast.error(errMsg(e)); }
  };
  const create = async () => {
    try { const p = await api("post", "/pages", { title: "New page", menu_label: `Page ${pages.length + 1}`, eyebrow: "", sections: [] }); load(p.id); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="pages-page">
      <PageHead title="Pages" text="Build the guest site: reorder and hide pages in the guest menu, and add text, images, galleries, swatches, buttons and files.">
        <button className="btn-ink !py-2 text-sm" onClick={create} data-testid="new-page-btn"><Plus size={15} /> Page</button>
      </PageHead>
      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        <div className="h-fit overflow-hidden rounded-2xl border border-[var(--line)] bg-white">
          <div className="border-b border-[var(--line)] px-4 py-2 text-xs text-slate-500">RSVP and Stay &amp; Transfers appear right after the Costume page.</div>
          {pages.map((p, i) => (
            <div key={p.id} className={`flex items-center gap-2 border-b border-[var(--line)] px-3 py-2.5 last:border-0 ${cur === p.id ? "bg-[var(--ivory-2)]" : ""}`} data-testid={`page-row-${p.slug}`}>
              <div className="flex flex-col">
                <button disabled={i === 0} onClick={() => move(i, -1)} className="text-slate-400 disabled:opacity-30" data-testid={`page-up-${p.slug}`}><ArrowUp size={13} /></button>
                <button disabled={i === pages.length - 1} onClick={() => move(i, 1)} className="text-slate-400 disabled:opacity-30" data-testid={`page-down-${p.slug}`}><ArrowDown size={13} /></button>
              </div>
              <button className={`flex-1 text-left text-sm ${p.parent ? "pl-4 text-slate-500 before:mr-1 before:content-['↳']" : ""} ${p.hidden ? "text-slate-400 line-through" : ""}`} onClick={() => setCur(p.id)} data-testid={`page-select-${p.slug}`}>{label(p)}</button>
              <button onClick={() => toggle(p)} className="p-1 text-slate-500" data-testid={`page-toggle-${p.slug}`}>{p.hidden ? <EyeOff size={15} /> : <Eye size={15} />}</button>
            </div>
          ))}
        </div>
        {page && <PageEditor page={page} onSaved={() => load(page.id)} onDelete={() => setDel(true)} targets={targets}
          parents={pages.filter((x) => !x.system && !x.parent && x.id !== page.id)} />}
      </div>
      <Confirm open={del} onOpenChange={setDel} title={`Delete “${page ? label(page) : ""}”?`} text="The page and all its sections are removed from the guest site." action="Delete"
        onConfirm={async () => { try { await api("delete", `/pages/${page.id}`); toast.success("Page deleted"); setDel(false); load(); } catch (e) { toast.error(errMsg(e)); } }} />
    </div>
  );
}
